import { useEffect } from "react";
import { AppLog } from "services";

import { StatusMsg } from "utils";
import { profileVerified } from "assets";
import { postedTimeAgo } from "utils/formatTimeSecondToHour";
import { ShimmerViewStories } from "components/app/shrimmer/Shrimmer";
import { IoIosSend } from "react-icons/io";
import { ChevronLeft, ChevronRight, Music, Pause, Play, Volume2, VolumeOffIcon, X } from "lucide-react";
import { Button } from "components/ui/button";

import { Skeleton } from "components/ui/skeleton";
import { Virtuoso } from "react-virtuoso";

import Stories from "react-insta-stories";
import ImageUrlProfileStory from "components/app/image-url/ImageUrlProfileStory";
import ViewStoriesAudioPlayer from "components/app/view-stories-audio-player/ViewStoriesAudioPlayer";
import ImageUrlProfile from "components/app/image-url/ImageUrlProfile";
import appStore from "store/appStore";
import useViewStoryStore from "store/useViewStoryStore";
import Marquee from "components/marquee/Marquee";

interface ViewStoryContentProps {
  userId: string | null;
  onClose: () => void;
}

export default function ViewStoryContent({ userId, onClose }: ViewStoryContentProps) {
  // * --- Variables --- * //
  const {
    users,
    usersAppRecord,
    usersStoriesAppRecord,
    initializePage,
    fetchProfileUserStory,
    currentUserIndex,
    currentStoryIndex,
    storyKey,
    isPaused,
    handleNext,
    handlePrev,
    togglePausePlay,
    onClicedUserStory,
  } = useViewStoryStore();

  const { isMuted, toggleMute } = appStore();

  // * --- Functions --- * //

  const loadMoreItems = () => {
    if (usersAppRecord.canLoadMore() === false || users.length === 0) {
      return;
    }

    setTimeout(async () => {
      await fetchProfileUserStory({ isRefresh: false });
    }, 5000);
  };

  // * --- Use Effects --- * //

  useEffect(() => {
    const loadInitialSuggestionFriends = async () => {
      try {
        await initializePage(userId);
      } catch (error) {
        AppLog.show({
          message: `[loadInitialReactions]`,
          status: StatusMsg.error,
          error,
        });
      }
    };
    loadInitialSuggestionFriends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // * --- Layouts --- * //
  const _profileStory = (user: any) => {
    return (
      <div className="absolute flex flex-row top-5 left-3 space-x-2 z-20 ">
        <ImageUrlProfile user={user} className="w-12 h-12 rounded-full object-cover border shadow-md " isLinkProfile={true} />
        <div className="flex flex-row justify-between items-center w-full">
          <div className="flex flex-col">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium text-white drop-shadow-[1px_1px_0px_rgba(0,0,0,0.5)]">{user.name}</p>
              {user.is_verified === "Yes" && <img src={profileVerified} alt="" style={{ width: 15, height: 15 }} />}

              {user.is_active !== "Yes" && (
                <p className="text-xs text-muted-foreground">
                  `$
                  {postedTimeAgo(user.last_active_datetime_human)}`
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <p className="text-xs text-white drop-shadow-[1px_1px_0px_rgba(0,0,0,0.5)]">{user.username}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen ">
      <div className="hidden md:block sticky top-[80px] left-0 z-0">
        <div className="w-[310px] lg:w-[310px]">
          <div className="p-4 space-y-2 flex flex-col justify-between h-[calc(100vh-90px)]">
            {usersAppRecord.isLoading() ? (
              <>
                <Skeleton className="w-full h-[40px] rounded-xl mt-4" />
              </>
            ) : (
              <div className="h-full space-y-2 mt-4">
                <div className="w-full pt-2">
                  <Button variant="secondary" size="icon" className="w-12 h-12 rounded-full top-1/2 transform -translate-y-1/2" onClick={onClose}>
                    <X className="w-6 h-6" />
                  </Button>
                </div>

                <Virtuoso
                  data={users}
                  endReached={loadMoreItems}
                  context={{ usersAppRecord }}
                  overscan={2}
                  itemContent={(index, user) => (
                    <div key={user.id} className={`my-2 p-1 rounded-lg cursor-pointer  ${index === currentUserIndex ? "bg-muted" : ""}`} onClick={() => onClicedUserStory(index)}>
                      <div className="flex flex-row  space-x-2  w-full  items-center ">
                        <ImageUrlProfileStory user={user} className="w-14 h-14" isLinkProfile={false} />

                        <div className="flex flex-row justify-between items-center w-full">
                          <div className="flex flex-col">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium ">{user.name}</p>
                              {user.is_verified === "Yes" && <img src={profileVerified} alt="" style={{ width: 15, height: 15 }} />}

                              {user.is_active !== "Yes" && (
                                <p className="text-xs text-muted-foreground">
                                  `$
                                  {postedTimeAgo(user.last_active_datetime_human)}`
                                </p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <p className="text-xs text-muted-foreground">{user.username}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  components={{
                    Footer: () =>
                      usersAppRecord.isLoadingMore() ? (
                        <div className="flex items-center space-x-4">
                          <Skeleton className="h-12 w-12 rounded-full" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-48" />
                            <Skeleton className="h-2 w-40" />
                          </div>
                        </div>
                      ) : usersAppRecord.isNoLoadMore() ? (
                        <div className="w-full flex justify-center items-center pt-2">
                          <b>Nothing more to load!</b>
                        </div>
                      ) : null,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-1 ">
        {usersStoriesAppRecord.isLoading() ? (
          <ShimmerViewStories />
        ) : users[currentUserIndex].stories ? (
          <div className="flex flex-col w-full overflow-auto">
            <div className="flex flex-grow rounded-lg relative overflow-auto select-none">
              <div className="flex flex-row  justify-center items-center p-2 space-x-4 w-full">
                <Button variant="secondary" size="icon" className="w-14 h-14 rounded-full top-1/2 transform -translate-y-1/2" onClick={() => handlePrev(currentStoryIndex)}>
                  <ChevronLeft className="w-8 h-8" />
                </Button>

                <div className="flex flex-col h-full aspect-[9/18] overflow-auto space-y-3 py-3">
                  <div className="flex flex-grow rounded-lg relative overflow-auto select-none">
                    {users && users.length > 0 ? (
                      <Stories
                        key={storyKey}
                        stories={users[currentUserIndex].stories}
                        width={"100%"}
                        height={"100%"}
                        currentIndex={currentStoryIndex}
                        onStoryEnd={() => handleNext(currentStoryIndex)}
                        onAllStoriesEnd={() => {}}
                        onPrevious={() => {}}
                        onNext={() => {}}
                        storyStyles={{}}
                        storyContainerStyles={{ pointerEvents: "none" }}
                        keyboardNavigation={false}
                        isPaused={isPaused}
                        onStoryStart={() => {
                          if (isPaused) {
                            togglePausePlay();
                          }
                        }}
                      />
                    ) : (
                      <> no data</>
                    )}
                    {users && users.length > 0 && _profileStory(users[currentUserIndex])}
                  </div>
                </div>

                <div className="flex flex-col h-full relative justify-center">
                  <Button variant="secondary" size="icon" className="w-14 h-14 rounded-full top-1/2 transform -translate-y-1/2" onClick={() => handleNext(currentStoryIndex)}>
                    <ChevronRight className="w-8 h-8" />
                  </Button>

                  <div className="absolute bottom-4 flex flex-col  space-y-2">
                    <Button variant="secondary" size="icon" className="w-12 h-12  rounded-full" onClick={togglePausePlay}>
                      {isPaused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
                    </Button>

                    {/* Button Mute Video */}
                    {users && users[currentUserIndex].stories[currentStoryIndex]?.post?.media_type.toLowerCase() === "video" && (
                      <Button variant="secondary" size="icon" className="w-12 h-12  rounded-full" onClick={toggleMute}>
                        {isMuted ? <VolumeOffIcon className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                      </Button>
                    )}

                    {users &&
                      users[currentUserIndex].stories[currentStoryIndex]?.post?.sound_url &&
                      users[currentUserIndex].stories[currentStoryIndex]?.post?.media_type.toLowerCase() === "video" && (
                        <div className="flex flex-row space-x-2 items-center w-full ">
                          <Button variant="secondary" size="icon" className="w-12 h-12  rounded-full">
                            <Music className="w-6 h-6" />
                          </Button>
                          <Marquee text={users[currentUserIndex].stories[currentStoryIndex]?.post?.sound_title} />
                        </div>
                      )}

                    {users &&
                      users[currentUserIndex].stories[currentStoryIndex]?.post?.sound_url &&
                      users[currentUserIndex].stories[currentStoryIndex]?.post?.media_type.toLowerCase() !== "video" && (
                        <ViewStoriesAudioPlayer
                          src={users[currentUserIndex].stories[currentStoryIndex]?.post?.sound_url}
                          title={users[currentUserIndex].stories[currentStoryIndex]?.post?.sound_title}
                          isPaused={isPaused}
                        />
                      )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-row justify-center items-center space-x-2 p-3 ">
              <div className="flex items-center space-x-2 bg-gray-800 px-3 py-2 rounded-lg ">
                <input type="text" placeholder="Reply..." className="bg-transparent outline-none text-white placeholder-gray-400 " />
                <IoIosSend className="cursor-pointer text-blue-500 text-xl" />
              </div>
            </div>
          </div>
        ) : (
          <ShimmerViewStories />
        )}
      </div>
    </div>
  );
}

